import {FC, useContext, useEffect, useState} from 'react'
import {format} from 'date-fns'
import LaborScheduleActualContext from '../../context/LaborScheduleActualContext'
import FdsModalContext from '../../../../../_fds/layout/ModalContext'
import {getScheduleWorkflowLog} from '../../core/_requests'
import {LaborScheduleActualModel, WorkflowLogEntryModel} from '../../core/_models'
import {toTitle} from '../../helpers/generals'

/* Approval-history icon shown left of the completion gauges. Rendered only
 * when the schedule has at least one workflow-log entry; clicking it opens
 * a modal with the full history (newest first, server-provided). */
interface Props {
  // The schedule, when rendered outside LaborScheduleActualContext (the
  // status group is portalled from the page toolbar).
  laborScheduleActual?: LaborScheduleActualModel | null
}

export const WorkflowHistoryButton: FC<Props> = (props) => {
  const fromContext = useContext(LaborScheduleActualContext).laborScheduleActual
  const laborScheduleActual =
    props.laborScheduleActual !== undefined ? props.laborScheduleActual : fromContext
  const {setSettings} = useContext(FdsModalContext)
  const [history, setHistory] = useState<WorkflowLogEntryModel[]>([])

  useEffect(() => {
    let cancelled = false
    if (!laborScheduleActual?.id) {
      setHistory([])
      return
    }
    getScheduleWorkflowLog(laborScheduleActual.id).then((rows) => {
      if (!cancelled) {
        setHistory(rows || [])
      }
    })
    return () => {
      cancelled = true
    }
    // refetch after every status change so the icon appears with the first action
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborScheduleActual?.id, laborScheduleActual?.workflow_status])

  if (!history.length) {
    return null
  }

  const openModal = () => {
    setSettings({
      cancelButtonText: 'Close',
      saveButtonText: '',
      saveButtonCallback: () => {},
      closeButtonCallback: () => {},
      titleText: 'Approval History',
      size: 'lg', // wide enough for four single-line columns + a wrapping Reason
      content: (
        // When/Action/Status/By shrink to their single-line content (width:1%
        // + nowrap); Reason absorbs all remaining width and wraps, but never
        // below its min-width — on tight screens the table scrolls inside the
        // modal (table-responsive) instead of crushing Reason to one
        // character per line.
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 w-100 fs-7'>
            <thead>
              <tr className='fw-bold text-gray-600'>
                <th className='text-nowrap' style={{width: '1%'}}>
                  When
                </th>
                <th className='text-nowrap' style={{width: '1%'}}>
                  Action
                </th>
                <th className='text-nowrap' style={{width: '1%'}}>
                  Status Change
                </th>
                <th className='text-nowrap' style={{width: '1%'}}>
                  By
                </th>
                <th style={{minWidth: '180px'}}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry, index) => (
                <tr key={index}>
                  <td className='text-nowrap'>
                    {entry.created_at ? format(new Date(entry.created_at), 'MM/dd/yy HH:mm') : '—'}
                  </td>
                  <td className='text-nowrap'>{toTitle(entry.action_key)}</td>
                  <td className='text-nowrap'>
                    {toTitle(entry.from_status)} → {toTitle(entry.to_status)}
                  </td>
                  <td className='text-nowrap'>{entry.actor_name || 'System'}</td>
                  <td style={{overflowWrap: 'anywhere', whiteSpace: 'normal', minWidth: '180px'}}>
                    {entry.reason || '—'}
                    {entry.acknowledged_warnings && entry.acknowledged_warnings.length > 0 && (
                      <div className='text-warning fs-8'>
                        Proceeded despite: {entry.acknowledged_warnings.map(toTitle).join(', ')}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
      show: true,
    })
  }

  return (
    <a
      onClick={openModal}
      title='Approval history'
      className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-3'
      style={{cursor: 'pointer'}}
    >
      <i className='bi bi-clock-history fs-3'></i>
    </a>
  )
}
